import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ImagePlus, Loader2, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { partnerApi, uploadPartnerFile } from "../../../services/partnerApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

const GALLERY_MAX = 6;
const GALLERY_MIN = 1;

const inputClass =
  "mt-1.5 w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20";
const labelClass = "text-sm font-medium text-gray-900";
const sectionClass =
  "rounded-2xl border border-emerald-100 bg-white/90 p-5 shadow-sm sm:p-6";

const SOCIAL_FIELDS = [
  {
    key: "instagram",
    label: "Instagram",
    placeholder: "https://instagram.com/yourschool or @yourschool",
  },
  {
    key: "youtube",
    label: "YouTube",
    placeholder: "https://youtube.com/@yourschool",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    placeholder: "https://linkedin.com/company/yourschool",
  },
  {
    key: "facebook",
    label: "Facebook",
    placeholder: "https://facebook.com/yourschool",
  },
  {
    key: "twitter",
    label: "X / Twitter",
    placeholder: "https://x.com/yourschool",
  },
];

export default function PartnerMediaStep() {
  const { partner, refreshPartner } = useAuth();
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [social, setSocial] = useState({
    instagram: "",
    youtube: "",
    linkedin: "",
    facebook: "",
    twitter: "",
  });

  const gallery = useMemo(
    () =>
      (partner?.assets || [])
        .filter((a) => a.kind === "gallery" && a.public_url)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [partner]
  );

  useEffect(() => {
    if (!partner?.org) return;
    setPreview(partner.org.logo_url || "");
    const links =
      partner.org.social_links && typeof partner.org.social_links === "object"
        ? partner.org.social_links
        : {};
    setSocial({
      instagram: links.instagram || "",
      youtube: links.youtube || "",
      linkedin: links.linkedin || "",
      facebook: links.facebook || "",
      twitter: links.twitter || "",
    });
  }, [partner]);

  async function onLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setSaving(true);
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      const asset = await uploadPartnerFile(file, "logo");
      if (!asset.public_url) throw new Error("Upload finished but no URL was returned.");
      setPreview(asset.public_url);
      URL.revokeObjectURL(localUrl);
      await refreshPartner();
    } catch (err) {
      setError(err.message || "Logo upload failed");
      setPreview(partner?.org?.logo_url || "");
      URL.revokeObjectURL(localUrl);
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  }

  async function onGalleryChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setGalleryError("");
    const remaining = GALLERY_MAX - gallery.length;
    if (remaining <= 0) {
      setGalleryError(`You can upload up to ${GALLERY_MAX} photos.`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    setGalleryUploading(true);
    try {
      for (const file of toUpload) {
        try {
          await uploadPartnerFile(file, "gallery");
        } catch (err) {
          setGalleryError(err.message || "One of the photos failed to upload.");
        }
      }
      await refreshPartner();
    } finally {
      setGalleryUploading(false);
    }
  }

  async function deleteGallery(id) {
    setDeletingId(id);
    setGalleryError("");
    try {
      await partnerApi.deleteAsset(id);
      await refreshPartner();
    } catch (err) {
      setGalleryError(err.message || "Could not delete photo");
    } finally {
      setDeletingId("");
    }
  }

  async function handleContinue() {
    setError("");
    if (gallery.length < GALLERY_MIN) {
      setError("Upload at least one gallery photo to continue.");
      return;
    }
    setSaving(true);
    try {
      await partnerApi.patchOrg({
        social_links: {
          instagram: social.instagram.trim(),
          youtube: social.youtube.trim(),
          linkedin: social.linkedin.trim(),
          facebook: social.facebook.trim(),
          twitter: social.twitter.trim(),
        },
      });
      await refreshPartner();
      navigate("/partner/onboarding/commercial");
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <OnboardingStepFrame
      title="Gallery, logo & social"
      subtitle="Add photos of your school or retreat space (required). Logo and social profiles are optional."
    >
      <div className="space-y-6">
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        <div className={sectionClass}>
          <p className={labelClass}>Gallery</p>
          <p className="mt-1 text-xs text-gray-500">
            Required — upload at least {GALLERY_MIN} photo (up to {GALLERY_MAX}).
          </p>
          {galleryError && (
            <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {galleryError}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-3">
            {gallery.map((a) => (
              <div key={a.id} className="relative h-20 w-20 overflow-hidden rounded-lg border">
                <img src={a.public_url} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  disabled={deletingId === a.id}
                  onClick={() => deleteGallery(a.id)}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={onGalleryChange}
          />
          <button
            type="button"
            disabled={galleryUploading || gallery.length >= GALLERY_MAX}
            onClick={() => galleryInputRef.current?.click()}
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {galleryUploading ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />}
            Add photos
          </button>
        </div>

        <div className={sectionClass}>
          <p className={labelClass}>Logo (optional)</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border border-emerald-100 bg-emerald-50/50">
              {preview ? (
                <img src={preview} alt="Logo preview" className="h-full w-full object-contain" />
              ) : (
                <ImagePlus className="text-emerald-400" size={28} />
              )}
            </div>
            <div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onLogoChange}
              />
              <button
                type="button"
                disabled={saving}
                onClick={() => logoInputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
              >
                {saving ? <Loader2 className="animate-spin" size={16} /> : null}
                {preview ? "Replace logo" : "Upload logo"}
              </button>
            </div>
          </div>
        </div>

        <div className={`${sectionClass} space-y-4`}>
          <div>
            <p className={labelClass}>Social profiles (optional)</p>
            <p className="mt-1 text-xs text-gray-500">
              Add any platforms you use — leave blank if you don’t have one.
            </p>
          </div>
          {SOCIAL_FIELDS.map((field) => (
            <label key={field.key} className="block">
              <span className="text-xs font-medium text-gray-700">{field.label}</span>
              <input
                className={inputClass}
                value={social[field.key]}
                onChange={(e) =>
                  setSocial((prev) => ({ ...prev, [field.key]: e.target.value }))
                }
                placeholder={field.placeholder}
              />
            </label>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/partner/onboarding/source"
            className="rounded-lg border border-emerald-200 px-4 py-2.5 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
          >
            Back
          </Link>
          <button
            type="button"
            disabled={saving}
            onClick={handleContinue}
            className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </OnboardingStepFrame>
  );
}
